import { useQueryClient } from "@tanstack/react-query";
import { authClient, useSession } from "../lib/auth.ts";
import { useMyOrgs } from "../lib/useMyOrgs.ts";

export function OrgSwitcher() {
  const { data: session, refetch } = useSession();
  const qc = useQueryClient();
  const { orgs } = useMyOrgs();
  const activeId = session?.session.activeOrganizationId ?? null;

  // Suspended orgs are hidden — a blocked org should look deleted to its
  // members (revertible only from the staff console). If the ACTIVE org got
  // blocked mid-session it won't be in the options; a disabled placeholder
  // keeps the select coherent while the user picks a live org.
  const visible = (orgs ?? []).filter((o) => !o.blocked);
  const activeHidden = activeId != null && !visible.some((o) => o.id === activeId);

  // Single-org users (the common case) get nothing rendered — the topbar
  // stays clean. With a hidden active org even one alternative is worth
  // showing: it's the user's way out.
  if (visible.length <= 1 && !(activeHidden && visible.length > 0)) return null;

  return (
    <select
      aria-label="Active organisation"
      value={activeHidden ? "" : activeId ?? ""}
      onChange={async (e) => {
        const id = e.target.value;
        if (!id || id === activeId) return;
        const r = await authClient.organization.setActive({ organizationId: id });
        if (r.error) {
          alert(`Failed to switch: ${r.error.message ?? "unknown"}`);
          return;
        }
        // disableCookieCache forces a fresh /get-session round-trip so
        // the new activeOrganizationId is visible immediately. Then nuke
        // the React Query cache so org-scoped queries (devices, tests,
        // releases) refetch under the new orgId.
        await refetch({ query: { disableCookieCache: true } });
        await qc.invalidateQueries();
      }}
      style={{ width: "auto", padding: "0.4rem 0.6rem" }}
    >
      {activeHidden && <option value="" disabled>Select organisation…</option>}
      {visible.map((o) => (
        <option key={o.id} value={o.id}>{o.name}</option>
      ))}
    </select>
  );
}
